"use client";

import WeeklySpecialsProduct from "@/app/(homepage)/weekly-specials/weeklySpecialsProduct";
import RecommendationsSlider from "@/app/ec/product/recommendations/recommendationsSlider";
import { useGetWeeklySpecialsQuery } from "@/store/baseApi";
import { getRecommendationsCarouselSlidesPerView } from "@/utils/getRecommendationsCarouselSlidesPerView";
import Link from "next/link";
import CaretRightIcon from "public/next-public/common/icons/caret-solid-right.svg";
import { useEffect, useMemo, useState } from "react";
import Countdown, { CountdownRenderProps } from "react-countdown";
import style from "./weeklySpecialsCountdown.module.scss";
import productStyle from "./weeklySpecialsProduct.module.scss";

const WEEKLY_SPECIALS_SLIDE_BREAKPOINTS = { 750: 3, 1000: 4, 1250: 5 } as const;

/** Bootstrap `md` — matches `d-md-flex` on the desktop recommendations carousel. */
const BOOTSTRAP_MD_MIN_PX = 768;

export default function WeeklySpecialsCountdown({ justMessage }: WeeklySpecialsCountdownProps) {
  const { data: weeklySpecials } = useGetWeeklySpecialsQuery();
  const weeklySpecialsEndTime = weeklySpecials?.endTime;
  const productCount = weeklySpecials?.products?.length ?? 0;
  const desktopPlaceholderCount = useWeeklySpecialsDesktopPadCount(productCount);

  return (
    <>
      {weeklySpecialsEndTime && (
        <section className={style.containsRecommendations}>
          <div className={style.sectionHeader}>
            <div className={style.seeAllSpacer}></div>
            <div className={style.titleBlock}>
              <span className={style.weeklySpecialsHeading}>
                {"Weekly Specials "}
                <span className={style.countdownWrap}>
                  <Countdown
                    date={weeklySpecialsEndTime}
                    renderer={(props) => {
                      return <WeeklySpecialsInteriorComponent {...props} justMessage={justMessage} />;
                    }}
                  />
                </span>
              </span>
            </div>
            <Link href="/weekly-specials" className={style.seeAllWeeklySpecials} prefetch={false}>
              See All
              <CaretRightIcon className={style.seeAllChevron} height={16} width={16} />
            </Link>
          </div>
          <RecommendationsSlider
            compactMobilePagination
            showLoadingRecommendations={false}
            rows={6}
            slideBreakpoints={WEEKLY_SPECIALS_SLIDE_BREAKPOINTS}
            desktopPlaceholderCount={desktopPlaceholderCount}
            renderDesktopPlaceholder={() => <WeeklySpecialsPlaceholderSlide />}
          >
            {weeklySpecials?.products.map((weeklySpecial, index) => {
              return <WeeklySpecialsProduct key={index} product={weeklySpecial} />;
            })}
          </RecommendationsSlider>
        </section>
      )}
    </>
  );
}
export type WeeklySpecialsCountdownProps = {
  justMessage?: boolean;
};

function WeeklySpecialsPlaceholderSlide() {
  return (
    <div className={`weekly-specials-product-container ${productStyle.weeklySpecialsProductContainer}`} aria-hidden>
      <div className={`weekly-specials-product ${productStyle.weeklySpecialsProduct}`} />
    </div>
  );
}

function useWeeklySpecialsDesktopPadCount(productCount: number): number {
  const [pad, setPad] = useState(0);

  useEffect(() => {
    const update = (): void => {
      if (productCount === 0) {
        setPad(0);
        return;
      }
      const w = window.innerWidth;
      if (w < BOOTSTRAP_MD_MIN_PX) {
        setPad(0);
        return;
      }
      const spv = getRecommendationsCarouselSlidesPerView(w, WEEKLY_SPECIALS_SLIDE_BREAKPOINTS);
      setPad(Math.max(0, spv - productCount));
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [productCount]);

  return pad;
}

function WeeklySpecialsInteriorComponent({
  days,
  hours,
  minutes,
  seconds,
  completed,
  justMessage,
}: WeeklySpecialsInteriorComponentProps) {
  /** This should be equal to "day" only when it is 1.  If it is 0, "days" is correct. */
  const dayOrDays = useMemo(() => (days == 1 ? "day" : "days"), [days]);
  const formattedHours = useMemo(() => hours.toString().padStart(2, "0"), [hours]);
  const formattedMinutes = useMemo(() => minutes.toString().padStart(2, "0"), [minutes]);
  const formattedSeconds = useMemo(() => seconds.toString().padStart(2, "0"), [seconds]);
  const shortMessage = useMemo(() => {
    return generateShortWeeklySpecials(days, hours, minutes, seconds);
  }, [days, hours, minutes, seconds]);

  if (completed === true) {
    return null;
  }

  if (justMessage) {
    return <>{shortMessage}</>;
  }
  return (
    <>
      <span className={`${style.wsCountdownDesktopVersion} ${style.weeklySpecialsCountdown}`}>
        Ends in {days} {dayOrDays} {formattedHours}:{formattedMinutes}:{formattedSeconds}
      </span>
      <span className={`${style.wsCountdownMobileVersion} ${style.weeklySpecialsCountdown}`}>
        Ends in {shortMessage}
      </span>
    </>
  );
}

type WeeklySpecialsInteriorComponentProps = CountdownRenderProps & { justMessage?: boolean };

export const generateShortWeeklySpecials = (days: number, hours: number, minutes: number, seconds: number): string => {
  if (days > 0) {
    const dayOrDays = days == 1 ? "day" : "days";
    return `${days} ${dayOrDays}`;
  } else if (hours > 0) {
    const hourOrHours = hours == 1 ? "hour" : "hours";
    return `${hours} ${hourOrHours}`;
  } else if (minutes > 0) {
    const minuteOrMinutes = minutes == 1 ? "minute" : "minutes";
    return `${minutes} ${minuteOrMinutes}`;
  } else if (seconds > 0) {
    const secondOrSeconds = seconds == 1 ? "second" : "seconds";
    return `${seconds} ${secondOrSeconds}`;
  } else {
    return "";
  }
};
